import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommunityProfiles, useUpdateCommunityProfileAnalytics } from '~/api/community';
import { communityProfileQueryOptions } from '~/api/community/useCommunityProfile';
import { Button, Dialog, ErrorState } from '~/components';
import { CommunityProfileDialog } from '~/features/community';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { breakpoints } from '~/styles/media.css';
import { clsx } from '~/styles/utils';

import { CommunityFilters } from './CommunityFilters';
import { footerStyle, gridPendingStyle, gridStyle, resultCountStyle } from './CommunityProfilesList.css';
import { CommunityProfileCard } from './components/CommunityProfileCard';
import { CommunityProfileCardSkeleton } from './components/CommunityProfileCard.skeleton';
import { useCommunityProfileFilters } from './NetworkOverview.nuqs';
import { ScrollToTopButton } from './ScrollToTopButton';

export const CommunityProfilesList = () => {
  const filters = useCommunityProfileFilters();

  // Only the query is debounced; the URL state updates on every keystroke.
  const [debouncedSearch] = useDebouncedState(filters.search.value, 200);

  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isPending, isSuccess, refetch } = useCommunityProfiles({
    name: debouncedSearch.trim(),
    activityIds: filters.activityIds.value,
    openToWork: filters.isOpenToWork.value,
    regions: filters.regions.value,
  });
  const queryClient = useQueryClient();
  const handleFetchNextPage = useCallback(() => !isFetchingNextPage && fetchNextPage(), [fetchNextPage, isFetchingNextPage]);
  const profiles = isSuccess ? data.pages.flatMap(({ data }) => data) : [];
  const totalResults = data?.pages.at(0)?.pagination.totalResults ?? null;

  const analyticsMutation = useUpdateCommunityProfileAnalytics('view');

  // Auto-load the next page when the "show more" button scrolls into view.
  const { ref: buttonRef } = useIntersectionObserver<HTMLButtonElement>(() => handleFetchNextPage(), { threshold: 0 });

  const { t } = useTranslation(['common', 'community']);
  const detailDialogHandle = Dialog.useHandle<number>();

  const isMobile = useMediaQuery(breakpoints.small);

  return (
    <>
      {!isMobile && <ScrollToTopButton />}

      <CommunityFilters />

      {!isError && (
        <div className={resultCountStyle}>
          <span>
            {totalResults === null ? t('community:overview.loading') : t('community:overview.resultCount', { count: totalResults })}
          </span>
        </div>
      )}

      {isError && <ErrorState onRetry={refetch} title={t('community:overview.errorLoading')} />}

      {(isPending || profiles.length > 0) && (
        <div className={clsx(gridStyle, isPending && gridPendingStyle)}>
          {isPending
            ? Array.from({ length: 12 }, () => null).map((_, i) => <CommunityProfileCardSkeleton animate key={i} />)
            : profiles.map((profile) => (
                <Dialog.Trigger
                  aria-label={t('community:profileCard.openDialog', { name: `${profile.firstName} ${profile.lastName}` })}
                  handle={detailDialogHandle}
                  key={profile.id}
                  nativeButton={false}
                  payload={profile.id}
                  render={(triggerProps) => (
                    <CommunityProfileCard
                      {...triggerProps}
                      onClick={(event) => {
                        triggerProps.onClick?.(event);
                        analyticsMutation.mutate(profile.id);
                        queryClient.setQueryData(communityProfileQueryOptions(profile.id).queryKey, profile);
                      }}
                      profile={profile}
                    />
                  )}
                />
              ))}
        </div>
      )}

      {hasNextPage && !isError && (
        <div className={footerStyle}>
          <Button emphasis="tertiary" loading={isFetchingNextPage} onClick={handleFetchNextPage} ref={buttonRef}>
            {!isFetchingNextPage && t('common:infiniteScroll.showMore')}
          </Button>
        </div>
      )}

      <Dialog.Root handle={detailDialogHandle}>
        {({ payload: communityProfileId }) =>
          communityProfileId !== undefined && <CommunityProfileDialog communityProfileId={communityProfileId} />
        }
      </Dialog.Root>
    </>
  );
};
